/**
 * TNCY-9 §d — Data Acquisitions is now a merged owner/member list. The BE
 * extended `GET /api/lyse/v1/data-acquisitions` (lyse/v1@8.1.0 / TNCY-8) with
 * a `scope=member` param + ownership metadata (`ownership`/`ownerTenantName`/
 * `role`) on the DA list row. This page (in the lyse app) fires both scopes
 * and merges client-side via the SHARED `mergeOwnerMember` primitive from the
 * cross-app `@aeriosiq/ownership` package — promoted in TNCY-9 out of
 * `apps/web/src/features/shared/ownership/` because the lyse app cannot import
 * from apps/web and `packages/ui` is barred for business vocabulary. Member
 * rows are DAs of member projects (roles exposing DAs — XT-D22) and carry the
 * `<OwnershipBadge>`. The ownership column only appears when the merged list
 * actually has member rows, so an own-only tenant's table is unchanged; a
 * customer-role caller sees no member rows (§e — the BE omits them).
 */
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactElement,
} from "react";
import {
  Autocomplete,
  Box,
  Button,
  FormControlLabel,
  IconButton,
  InputAdornment,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
  type SelectChangeEvent,
} from "@mui/material";
import {
  DataGrid,
  type GridColDef,
  type GridPaginationModel,
  type GridSortDirection,
  type GridSortModel,
} from "@mui/x-data-grid";
import { PageHeading, SingleSelectFilter, type Density } from "@aeriosiq/ui";
import { ownershipColumn } from "@aeriosiq/ownership";
import {
  DATA_ACQUISITION_STATES,
  type AssetSummary,
  type DataAcquisition,
  type DataAcquisitionSortKey,
  type DataAcquisitionState,
  type GetDataAcquisitionsParams,
  type ProjectSummary,
  type SiteSummary,
  type UserSummary,
} from "@aeriosiq/types";
import { useOwnerMemberDataAcquisitions } from "./useOwnerMemberDataAcquisitions.js";
import {
  SORT_DEFAULT,
  useDataAcquisitionsFilters,
  type DataAcquisitionsFilterState,
} from "./filters.js";
import { formatInspectionState } from "./stateMapping.js";
import { DataAcquisitionStatusChip } from "./DataAcquisitionStatusChip.js";
import { DatasetCacheCell } from "./DatasetCacheCell.js";
// DI-3-5 — the analyst's flight chips (lyse/v1 8.6.0 `flights`).
import { FlightChips } from "./FlightChips.js";
import { installCacheTestBridge } from "../analysis/cache/cacheTestBridge.js";
import { AutocompleteInput } from "./AutocompleteInput.js";
import { AssigneeChip } from "./AssigneeChip.js";
import { AssigneeFilter } from "./AssigneeFilter.js";
// R58-W-11 — Edit / Claim & Edit / View per-row action.
import { RowActionsCell } from "./RowActionsCell.js";
// DL-01 — "Download images" to a folder on this PC (owner roles; customers
// per the project's flag).
import { DownloadImagesCell } from "./DownloadImagesCell.js";
import { showDownloadColumn } from "../image-download/eligibility.js";
// R58-W-11 — useMe for myUserId + myRoles to drive the label.
import { useMe } from "@aeriosiq/auth-client";
import {
  ColumnVisibilityButton,
  type ColumnDescriptor,
} from "./ColumnVisibilityButton.js";
import { DateRangeButton } from "./DateRangeButton.js";
import { ExportButton } from "./ExportButton.js";
import { ProjectFilter } from "./ProjectFilter.js";
import { SiteFilter } from "./SiteFilter.js";
import {
  collectAssetIds,
  collectProjectIds,
  collectSiteIds,
  collectUserIds,
  formatAsset,
  formatDate,
  formatProject,
  formatSite,
  formatUser,
  indexById,
  useAssetsByIds,
  useProjectsByIds,
  useSitesByIds,
  useUsersByIds,
} from "./enrichment.js";
import { useNavigate } from "react-router-dom";
import { SentBackNotice } from "./SentBackNotice.js";
import {
  InboxIcon,
  LayoutGridIcon,
  Rows3Icon,
  SearchIcon,
  XIcon,
} from "@aeriosiq/ui/icons";

function EmptyState({ onClear }: { onClear: () => void }) {
  return (
    <Box
      role="region"
      aria-label="No data acquisitions"
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        py: 8,
        gap: 2,
        textAlign: "center",
      }}
    >
      <InboxIcon sx={{ fontSize: 48, color: "text.secondary" }} aria-hidden />
      <Typography variant="h6" component="p">
        No data acquisitions match these filters
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Try widening the date range or clearing a filter.
      </Typography>
      <Button variant="contained" onClick={onClear}>
        Clear filters
      </Button>
    </Box>
  );
}

function ErrorCard({
  errorId,
  onRetry,
}: {
  errorId: string;
  onRetry: () => void;
}) {
  return (
    <Paper
      role="alert"
      aria-live="assertive"
      aria-atomic="true"
      aria-label="Error"
      sx={{ p: 3, display: "flex", flexDirection: "column", gap: 1 }}
    >
      <Typography variant="h6" component="h2">
        Couldn&apos;t load data acquisitions
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Something went wrong while fetching the list. Try again in a moment.
      </Typography>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ fontFamily: "monospace" }}
        data-testid="error-id"
      >
        errorId: {errorId}
      </Typography>
      <Box>
        <Button variant="contained" onClick={onRetry}>
          Retry
        </Button>
      </Box>
    </Paper>
  );
}

/**
 * Page heading — bare `<PageHeading>` H1 (Story 055 AC1, 2026-05-13).
 * The count badge that Story 051 AC7 introduced is removed; the
 * Sites-style footer carries the total instead. Wrapping `<Box>`
 * gives downstream tests a stable parent locator (`h1.locator("..")`)
 * so the negative "no badge in header" assertion has something to
 * scope against.
 */
function PageHeadingRow(): ReactElement {
  return (
    <Box sx={{ display: "flex", alignItems: "center" }}>
      <PageHeading>Data Acquisitions</PageHeading>
    </Box>
  );
}

// Story 017 — DataGrid column field ↔ contract sort_by enum. Covers both
// numeric/date columns (sorted SQL-side) and the string-name columns
// (asset / park / project / pilot / turbine) which the dev-api / BE
// resolve via cross-DB enrichment + in-memory sort.
const FIELD_TO_SORT_KEY: Record<string, DataAcquisitionSortKey> = {
  instructionDate: "instruction_date",
  plannedDate: "planned_date",
  // SB3-CC-G (lyse/v1@6.0.0): the Status column now reads `state`
  // (unified single-axis). The previous `evaluationState` field +
  // `evaluation_state` sort key are retired per I5 supersedure.
  state: "state",
  maxSeverity: "max_severity",
  assetId: "asset_identifier",
  siteId: "site_name",
  projectId: "project_name",
  pilotUserId: "pilot_name",
  turbineType: "turbine_type",
  assignedTo: "assignee",
};
const SORT_KEY_TO_FIELD: Partial<Record<DataAcquisitionSortKey, string>> = {
  instruction_date: "instructionDate",
  planned_date: "plannedDate",
  state: "state",
  max_severity: "maxSeverity",
  asset_identifier: "assetId",
  site_name: "siteId",
  project_name: "projectId",
  pilot_name: "pilotUserId",
  turbine_type: "turbineType",
  assignee: "assignedTo",
};

/** DI-3-5 — the Flights column's field; sorted on the page, never server-side. */
const FLIGHTS_FIELD = "flightCount";

/** `flightCount`, else the array's length; null when the flights read failed. */
function flightCountOf(row: DataAcquisition): number | null {
  return row.flightCount ?? row.flights?.length ?? null;
}

/** Orders a page by flight count; rows without a count sort last either way. */
function sortByFlightCount(
  rows: readonly DataAcquisition[],
  dir: "asc" | "desc",
): DataAcquisition[] {
  return [...rows].sort((a, b) => {
    const x = flightCountOf(a);
    const y = flightCountOf(b);
    if (x === y) return 0;
    if (x === null) return 1;
    if (y === null) return -1;
    return dir === "asc" ? x - y : y - x;
  });
}

// Stage 9 — 12 columns matching
// .knowledge/eleks_platform/frontend_recovery/screenshots/001_aero_enterprise_gmbh_inspections.png
function buildColumns(
  sitesById: ReturnType<typeof indexById<SiteSummary>>,
  assetsById: ReturnType<typeof indexById<AssetSummary>>,
  projectsById: ReturnType<typeof indexById<ProjectSummary>>,
  usersById: ReturnType<typeof indexById<UserSummary>>,
  onView: (id: string) => void,
  myUserId: string | null | undefined,
  myRoles: readonly string[] | undefined,
  // TNCY-9 §d — prepend the cross-tenant ownership tell only when the merged
  // list actually has member rows, so an own-only tenant's table is
  // byte-for-byte the pre-TNCY-9 layout.
  showOwnership: boolean,
): GridColDef<DataAcquisition>[] {
  return [
    ...(showOwnership
      ? [ownershipColumn<DataAcquisition>({ entityNoun: "data acquisition" })]
      : []),
    {
      field: "assetId",
      headerName: "Asset",
      width: 200,
      renderCell: (p) =>
        p.row.assetId
          ? formatAsset(assetsById.get(p.row.assetId), p.row.assetId)
          : "—",
      sortable: true,
    },
    {
      field: "siteId",
      headerName: "Park",
      width: 180,
      renderCell: (p) =>
        p.row.siteId
          ? formatSite(sitesById.get(p.row.siteId), p.row.siteId)
          : "—",
      sortable: true,
    },
    {
      field: "projectId",
      headerName: "Project",
      width: 200,
      // FCII — a field-created DA's parent II has no project. Show a muted
      // "Unlinked" (decision C.7) rather than an em-dash; filterable via the
      // "Link status" toolbar filter → linkage=unlinked.
      renderCell: (p) =>
        p.row.projectId ? (
          formatProject(projectsById.get(p.row.projectId), p.row.projectId)
        ) : (
          <Typography variant="body2" component="span" color="text.secondary">
            Unlinked
          </Typography>
        ),
      sortable: true,
    },
    {
      field: "pilotUserId",
      headerName: "Pilot",
      width: 160,
      renderCell: (p) =>
        p.row.pilotUserId
          ? formatUser(usersById.get(p.row.pilotUserId), p.row.pilotUserId)
          : "—",
      sortable: true,
    },
    {
      field: "turbineType",
      // R58-W-08 (R58-12) — renamed from "Turbine Type" to "Turbine
      // Model" so the column matches the canonical
      // `Assets → Turbine Models` page label. Label-only change; the
      // underlying field (`asset.modelName`) is unchanged.
      headerName: "Turbine Model",
      width: 140,
      // Synthetic column — derived from asset.modelName.
      valueGetter: (_v, row) =>
        row.assetId ? (assetsById.get(row.assetId)?.modelName ?? "—") : "—",
      sortable: true,
    },
    // R58-W-08 (R58-16) — the "Planned For" column is removed. The
    // Inspected On column below is the primary date column and the
    // default ascending sort. The `plannedDate` field is still
    // returned on the wire (no contract break — see contract 5.4.0
    // CHANGELOG §Out of scope).
    {
      field: "instructionDate",
      headerName: "Inspected On",
      width: 140,
      renderCell: (p) => formatDate(p.row.instructionDate),
      // R58-W-08 (R58-16) — primary date column, default sort
      // `instructionDate asc` (see SORT_DEFAULT below).
      sortable: true,
    },
    {
      field: "maxSeverity",
      headerName: "Severity",
      width: 100,
      align: "center",
      headerAlign: "left",
      renderCell: (p) =>
        p.row.maxSeverity == null ? "—" : String(p.row.maxSeverity),
      sortable: true,
    },
    {
      // SB3-CC-G (lyse/v1@6.0.0): unified single-axis `state` field per
      // I5 supersedure. Carries both lifecycle values (CREATED, PROCESSING)
      // and analyst sub-workflow (PRE_SCREENING, EXPERT_ANALYSIS,
      // EVALUATION_DONE, LOCKED) that used to live on `evaluationState`.
      field: "state",
      headerName: "Status",
      width: 160,
      // SB6-DA-01 — per-state Apple-colour bands (mirrors the Inspection
      // Instruction chip) instead of the generic 5-bucket StatusPill.
      renderCell: (p) => <DataAcquisitionStatusChip state={p.row.state} />,
      sortable: true,
    },
    {
      // DI-3-5 — flights · enabled · offset + route · images (D91 chips). No
      // blob-state chip on the list: the counts are a detail read. lyse/v1
      // 8.6.0 has no `flight_count` sort key, so this column orders the
      // CURRENT PAGE client-side (see `FLIGHTS_FIELD` in the feature body).
      field: FLIGHTS_FIELD,
      headerName: "Flights",
      width: 360,
      valueGetter: (_v, row) => flightCountOf(row),
      renderCell: (p) => <FlightChips row={p.row} />,
      sortable: true,
    },
    {
      field: "assignedTo",
      headerName: "Assignee",
      width: 200,
      renderCell: (p) => (
        <AssigneeChip
          inspectionId={p.row.id}
          current={
            p.row.assignedTo ? usersById.get(p.row.assignedTo) : undefined
          }
          fallback={p.row.assignedTo ?? "Unassigned"}
        />
      ),
      // R58-W-08 (R58-15) — Assignee column is now sortable. Maps to
      // `sort_by=assignee` per contract 5.4.0; the BE applies the
      // PM-locked "unassigned-first" ordering (stable across asc/desc).
      sortable: true,
    },
    {
      // SB6-ANA-CACHE-03 — per-row image-cache completeness (cached/total),
      // derived from the client-side cache manifest. Live-updates while a
      // download runs (the cell subscribes to the manifest store); pinned
      // state shown on the same cell. Client-only — never server-sortable.
      // ANA-R4-DA-01 — moved to sit between Assignee and Action so the cache
      // count is visible right before the action button.
      field: "__cached",
      headerName: "Cached",
      width: 110,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      align: "left",
      renderCell: (p) => <DatasetCacheCell dataAcquisitionId={p.row.id} />,
    },
    ...(showDownloadColumn(myRoles, showOwnership)
      ? [
          {
            // DL-01 — the full-resolution images of the row's data set written
            // to a folder on this PC (story §B, "the DA table"). The column
            // exists only when a row could carry the action; within it the
            // cell decides per row — an ineligible row renders nothing, never
            // a disabled icon.
            field: "__download",
            headerName: "Download",
            width: 90,
            sortable: false,
            filterable: false,
            disableColumnMenu: true,
            align: "center",
            headerAlign: "center",
            renderCell: (p) => (
              <DownloadImagesCell row={p.row} myRoles={myRoles} />
            ),
          } satisfies GridColDef<DataAcquisition>,
        ]
      : []),
    {
      field: "__actions",
      headerName: "Action",
      // R58-W-11 — wider to fit "Claim & Edit" without truncation.
      width: 140,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      // R58-W-11 — Edit / Claim & Edit / View button per the OQ-6
      // decision matrix. The previous bare VIEW button is replaced by
      // RowActionsCell which derives the label from
      // (row.assignedTo, myUserId, myRoles) and threads the claim
      // mutation through PATCH /data-acquisitions/{id}/assignee on
      // self-claim.
      renderCell: (p) => (
        <RowActionsCell
          row={p.row}
          myUserId={myUserId}
          myRoles={myRoles}
          onOpen={onView}
        />
      ),
    },
  ];
}

function buildQueryParams(
  filters: DataAcquisitionsFilterState,
): GetDataAcquisitionsParams {
  const params: GetDataAcquisitionsParams = {
    page: filters.page,
    pageSize: filters.pageSize,
  };
  if (filters.q) params.q = filters.q;
  if (filters.state.length > 0) params.state = filters.state;
  if (filters.project_id.length > 0) params.project_id = filters.project_id;
  if (filters.site_id.length > 0) params.site_id = filters.site_id;
  if (filters.assignee_id.length > 0) params.assignee_id = filters.assignee_id;
  // SB6-DA-01 — "Show completed". Only set when on so the BE default
  // (hide LOCKED + REJECTED) applies otherwise.
  if (filters.includeCompleted) params.include_completed = true;
  // FCII — orphan filter. Only set the two engaging values; `all` sends nothing.
  if (filters.linkage !== "all") params.linkage = filters.linkage;
  if (filters.date_from) params.date_from = filters.date_from;
  if (filters.date_to) params.date_to = filters.date_to;
  // Story 017 — server-side single-column sort. When the URL has no
  // sortBy, fall back to SORT_DEFAULT (instruction_date desc) so the
  // server returns the same default ordering AC4 promises.
  const effectiveSortBy = filters.sortBy ?? SORT_DEFAULT.sortBy;
  const effectiveSortOrder = filters.sortOrder ?? SORT_DEFAULT.sortOrder;
  params.sort_by = [effectiveSortBy];
  params.sort_order = [effectiveSortOrder];
  return params;
}

/**
 * Debounce delay for the search input (ms). User feedback 2026-05-08:
 * synchronous re-render on every keystroke caused dropped characters
 * while typing fast. 400 ms feels responsive but lets a user finish
 * typing a 6-letter word ("Vestas", "Bünne") before the list updates.
 */
const SEARCH_DEBOUNCE_MS = 400;

/** localStorage key for the column-visibility model (Theme-A polish). */
const COLUMNS_STORAGE_KEY = "@aeriosiq/lyse/inspections/columns/v1";

/** DI-3-S38 (item 6) — the search box's words: fits the 280 px box whole. */
const SEARCH_PLACEHOLDER = "Code, turbine, pilot or set id";
/** DI-3-S38 (item 6) — the full sentence, on the field as its title. */
const SEARCH_TITLE = "Search — code, turbine, site, pilot or the set's id";

export function DataAcquisitionsListFeature() {
  // SB6-ANA-CACHE-03 — the image-cache test bridge is also mounted here so the
  // acceptance spec can drive the live cached-% update (AC6) on the list page.
  // No production code reads `window.__aeroCacheBridge`.
  useEffect(() => installCacheTestBridge(), []);

  const { filters, setFilters, clear, hasActiveFilters } =
    useDataAcquisitionsFilters();
  // TNCY-9 §d — fire both scopes + merge client-side; member rows carry the
  // `<OwnershipBadge>`. Own is the spine (its pending/error drives the list).
  const query = useOwnerMemberDataAcquisitions(buildQueryParams(filters));
  // Christian, 2026-08-13 smoke test: standard is the better DEFAULT for this
  // table. Compact packs the rows to 36px, which leaves an action control
  // almost touching the grid lines above and below it; the toggle is still
  // here, so anyone who wants the dense view is one click away.
  //
  // Note this is the DataGrid's own row-density preference, NOT the D77
  // surface density — they are different axes that unfortunately share a word.
  // `AppChrome`'s `<main data-density="compact">` governs control heights on
  // this page, EXCEPT inside the filter row, which declares itself comfortable
  // (see the toolbar below). This preference only governs how much vertical
  // room a table row gets.
  const [density, setDensity] = useState<Density>("standard");
  const navigate = useNavigate();

  // Search ergonomics — local state for the input box plus a debounced
  // write to URL state so the DataGrid only re-fetches once the user
  // pauses typing. A change initiated externally (Clear filters, deep
  // link with `?q=…`) overwrites the local input via the effect below.
  const [searchInput, setSearchInput] = useState(filters.q);
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    // While a debounce is PENDING the user is mid-typing and the input owns
    // the value: a `filters.q` change landing in this window is either the
    // echo of our own pending write or a change that write is about to
    // supersede. Syncing it back would overwrite characters typed since.
    //
    // This is not theoretical — it ate a character in the real app and made
    // `001 AC5` a CI flake. Under load the timer fired between a keystroke and
    // its commit, so the echo carried a value one character older than the
    // input, and typing "5550007" produced "550007". The input is controlled,
    // so the stale write also rewound the DOM value that `userEvent` types
    // against, which is why exactly one character went missing.
    if (debounceTimer.current !== null) return;
    setSearchInput(filters.q);
  }, [filters.q]);
  const handleSearchInput = useCallback(
    (next: string) => {
      setSearchInput(next);
      if (debounceTimer.current !== null) clearTimeout(debounceTimer.current);
      debounceTimer.current = setTimeout(() => {
        // Clear BEFORE writing so the effect above treats anything that lands
        // after this point as external again.
        debounceTimer.current = null;
        setFilters({ q: next });
      }, SEARCH_DEBOUNCE_MS);
    },
    [setFilters],
  );
  // "Clear filters" is external, and must also drop a pending debounce —
  // otherwise the in-flight write lands 400 ms later and restores the query
  // the user just cleared.
  const clearFilters = useCallback(() => {
    if (debounceTimer.current !== null) {
      clearTimeout(debounceTimer.current);
      debounceTimer.current = null;
    }
    setSearchInput("");
    clear();
  }, [clear]);
  // Clear pending timer on unmount so we don't write filters after the
  // component is gone.
  useEffect(() => {
    return () => {
      if (debounceTimer.current !== null) clearTimeout(debounceTimer.current);
    };
  }, []);

  // Theme-A polish (2026-05-08) — column visibility persisted to
  // localStorage. Browser-scoped (not tenant-scoped) per user feedback:
  // a workstation analyst doesn't switch tenants. Migration path: when
  // Story 036 ships a `getUserPreferences` BE endpoint, we sync this
  // value into the user profile so it follows the analyst across
  // devices.
  const [columnVisibilityModel, setColumnVisibilityModel] = useState<
    Record<string, boolean>
  >(() => {
    if (typeof window === "undefined") return {};
    try {
      const raw = window.localStorage.getItem(COLUMNS_STORAGE_KEY);
      if (!raw) return {};
      const parsed = JSON.parse(raw) as unknown;
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
        return parsed as Record<string, boolean>;
      }
      return {};
    } catch {
      return {};
    }
  });
  const handleColumnVisibilityChange = useCallback(
    (next: Record<string, boolean>) => {
      setColumnVisibilityModel(next);
      if (typeof window !== "undefined") {
        try {
          window.localStorage.setItem(
            COLUMNS_STORAGE_KEY,
            JSON.stringify(next),
          );
        } catch {
          // localStorage may be unavailable (private mode, quota exceeded);
          // the in-memory state still works for the rest of the session.
        }
      }
    },
    [],
  );

  const paginationModel = useMemo<GridPaginationModel>(
    () => ({ page: filters.page - 1, pageSize: filters.pageSize }),
    [filters.page, filters.pageSize],
  );

  // Story 017 — DataGrid drives sort via `sortModel`. URL is the source of
  // truth: the model is derived from filters.sortBy/sortOrder (or the server
  // default when both are null). Click the header → 3-state cycle (asc →
  // desc → unsorted) handled by MUI; we translate the resulting model back
  // to (sortBy, sortOrder) URL params on every change.
  // DI-3-5 — the Flights column sorts the current page only (no lyse/v1
  // sort key); while it is set it owns the grid's sort model.
  const [flightSort, setFlightSort] = useState<GridSortDirection>(null);
  const sortModel = useMemo<GridSortModel>(() => {
    if (flightSort) return [{ field: FLIGHTS_FIELD, sort: flightSort }];
    const key = filters.sortBy ?? SORT_DEFAULT.sortBy;
    const order = filters.sortOrder ?? SORT_DEFAULT.sortOrder;
    const field = SORT_KEY_TO_FIELD[key];
    if (!field) return [];
    return [{ field, sort: order }];
  }, [filters.sortBy, filters.sortOrder, flightSort]);

  const handleSortModelChange = useCallback(
    (model: GridSortModel) => {
      const head = model[0];
      if (head?.field === FLIGHTS_FIELD) {
        setFlightSort(head.sort ?? null);
        return;
      }
      if (flightSort) {
        // Leaving the page-local sort: back to the server's order first.
        setFlightSort(null);
        if (!head?.sort) return;
      }
      if (!head?.sort) {
        // 3rd click of the cycle → return to default (clear URL params).
        setFilters({ sortBy: null, sortOrder: null });
        return;
      }
      const sortKey = FIELD_TO_SORT_KEY[head.field];
      if (!sortKey) {
        // Field isn't server-sortable; ignore the model change.
        return;
      }
      setFilters({ sortBy: sortKey, sortOrder: head.sort });
    },
    [setFilters, flightSort],
  );

  // Memoize so siteIds / assetIds derivations are stable across renders.
  const items = useMemo<DataAcquisition[]>(
    () => (flightSort ? sortByFlightCount(query.rows, flightSort) : query.rows),
    [query.rows, flightSort],
  );
  const total = query.total;
  const hasMemberRows = query.hasMemberRows;

  // Enrichment: collect unique site / asset / project / user UUIDs from the
  // current page, fetch human-readable names in parallel, and merge
  // in-process. Failures fall back to the raw UUID so the grid stays usable.
  const siteIds = useMemo(() => collectSiteIds(items), [items]);
  const assetIds = useMemo(() => collectAssetIds(items), [items]);
  const projectIds = useMemo(() => collectProjectIds(items), [items]);
  const userIds = useMemo(() => collectUserIds(items), [items]);
  const sitesQuery = useSitesByIds(siteIds);
  const assetsQuery = useAssetsByIds(assetIds);
  const projectsQuery = useProjectsByIds(projectIds);
  const usersQuery = useUsersByIds(userIds);
  const sitesById = useMemo(
    () => indexById(sitesQuery.data?.items),
    [sitesQuery.data],
  );
  const assetsById = useMemo(
    () => indexById(assetsQuery.data?.items),
    [assetsQuery.data],
  );
  const projectsById = useMemo(
    () => indexById(projectsQuery.data?.items),
    [projectsQuery.data],
  );
  const usersById = useMemo(
    () => indexById(usersQuery.data?.items),
    [usersQuery.data],
  );
  const handleView = useCallback(
    (id: string) => {
      void navigate(`/analysis/${id}`);
    },
    [navigate],
  );
  // R58-W-11 — feed the action-cell my user-id + roles so it can
  // render the Edit / Claim & Edit / View label.
  const me = useMe();
  const myUserId = me.data?.id ?? null;
  const myRoles = me.data?.roles ?? undefined;
  const columns = useMemo(
    () =>
      buildColumns(
        sitesById,
        assetsById,
        projectsById,
        usersById,
        handleView,
        myUserId,
        myRoles,
        hasMemberRows,
      ),
    [
      sitesById,
      assetsById,
      projectsById,
      usersById,
      handleView,
      myUserId,
      myRoles,
      hasMemberRows,
    ],
  );
  // Descriptors fed into the toolbar's Columns button. Action stays
  // pinned (always visible) so users can't lock themselves out of the
  // row-open affordance.
  const columnDescriptors = useMemo<ColumnDescriptor[]>(
    () =>
      columns.map(
        (c): ColumnDescriptor => ({
          field: c.field,
          headerName: c.headerName ?? c.field,
          ...(c.field === "__actions" ? { pinned: true as const } : {}),
        }),
      ),
    [columns],
  );
  // Empty states render a static panel instead of a zero-row DataGrid —
  // role="grid" with no role="row" children trips axe's aria-required-children.
  const isEmpty = !query.isPending && !query.isError && items.length === 0;
  const isEmptyAfterLoad = isEmpty && hasActiveFilters;
  const isInitialEmpty = isEmpty && !hasActiveFilters;

  if (query.isError) {
    const errorId =
      (query.error as { errorId?: string } | undefined)?.errorId ?? "unknown";
    return (
      <Box
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
        }}
      >
        <Box sx={{ flexShrink: 0, mb: 2 }}>
          <PageHeadingRow />
        </Box>
        <ErrorCard
          errorId={errorId}
          onRetry={() => {
            query.refetch();
          }}
        />
      </Box>
    );
  }

  // Story 055 — Sites-style polish.
  //   - Column-flex container that fills `<main>` exactly so the page
  //     never double-scrolls (mirrors /sites' Story 054 AC21 fix).
  //   - Two-row toolbar: filters above, table-settings below, with a
  //     divider line between them (mirrors /sites' AC25 + AC27).
  //   - Custom Sites-style footer: total LEFT, page-size + Pagination
  //     RIGHT (mirrors /sites' Story 054 AC23). MUI X's stock
  //     TablePagination footer is hidden via `hideFooter` on the grid.
  return (
    <Box
      sx={{
        height: "100%",
        width: "100%",
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
        // Christian, 2026-05-13: *"When I scroll the canvas to the
        // right, the whole canvas scrolls. I expect only the table to
        // scroll horizontally. Filter elements + table settings should
        // stick to the upper-left corner, pagination to the lower-
        // right corner, the total count to the lower-left corner."*
        //
        // Containing horizontal overflow here is the load-bearing fix:
        // the MUI X DataGrid, when its columns exceed viewport width,
        // can push its parent wider than the column-flex page wrapper
        // and trigger page-level horizontal scroll (the toolbar +
        // footer then move sideways with the user's scroll wheel).
        // `minWidth: 0` lets the flex container shrink below content
        // size; `overflowX: hidden` clips any residual spill. The
        // DataGrid then has no choice but to engage its OWN internal
        // horizontal scroller for its rows, leaving the toolbar +
        // footer pinned to the page edges.
        minWidth: 0,
        overflowX: "hidden",
      }}
    >
      {/* Page header — bare H1, no count badge (AC1). */}
      <Box sx={{ flexShrink: 0, mb: 2, minWidth: 0 }}>
        <PageHeadingRow />
      </Box>

      {/* Toolbar (2 rows) — mirrors /sites:
            row 1 = filter controls (search + filter chips),
            row 2 = table-settings (Columns + Density + Export). */}
      <Box
        role="toolbar"
        aria-label="Data acquisitions toolbar"
        sx={{
          flexShrink: 0,
          mb: 2,
          display: "flex",
          flexDirection: "column",
          // Explicit full width — Christian, 2026-05-13: *"the grey
          // separation line below filter settings is shorter than our
          // actual table width."* Without the explicit width, the
          // toolbar (and its row 2's borderTop divider) can collapse
          // below the parent's cross-axis size under certain layout
          // shifts. Pinning width:100% guarantees the divider spans the
          // full table column.
          width: "100%",
        }}
      >
        {/* Row 1 — search left, filter chips next, wraps on narrow
            viewports. The filter-pill widths are uniform 280 px (see
            ProjectFilter / SiteFilter / Status Autocomplete /
            AssigneeFilter + the local Search TextField), so picking
            multiple values tiles the chips inside the field (height
            grows) instead of widening the field, AND when the row
            wraps it forms a uniform grid: 5+0 at wide viewports,
            3+2 at the typical 1440 viewport, 2+2+1 narrower. The
            symmetric widths keep every wrap rhythm clean.
            `useFlexGap` switches MUI Stack from the legacy margin-
            based spacing (which leaves no row-gap on wrapped rows)
            to the modern CSS `gap` property — load-bearing for the
            vertical spacing between wrapped filter rows. Without it,
            Status sat flush against Project below it with zero
            vertical breathing room (Christian's screenshot,
            2026-05-13). */}
        <Stack
          data-testid="data-acquisitions-toolbar-filters"
          // D77 — this REGION is comfortable even though the app around it is
          // a compact surface. On this page the filter row is the tool: the
          // operator is here to narrow a fleet of acquisitions, not to read a
          // cell, and eight controls that each need reading deserve the full
          // target (Christian, 2026-09-23). The table below stays dense.
          //
          // The ten sibling list pages say the same thing through
          // `DataPageToolbar`'s `comfortableFilters`; this page builds its own
          // row, so it says it directly.
          data-density="comfortable"
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          useFlexGap
          sx={{
            width: "100%",
            alignItems: { sm: "center" },
            flexWrap: "wrap",
          }}
        >
          <TextField
            value={searchInput}
            onChange={(e) => {
              handleSearchInput(e.target.value);
            }}
            // DI-3-S38 (item 6, lyse/v1 8.6.1) — the words name what `q`
            // matches, the set's id (the one the pilot copies) included. The
            // placeholder is the longest form measured to fit the 280 px box
            // whole (216.8 of 222 px); the full sentence is the field's title
            placeholder={SEARCH_PLACEHOLDER}
            size="small"
            slotProps={{
              htmlInput: {
                "aria-label": "Search",
                title: SEARCH_TITLE,
                type: "search",
              },
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon
                      fontSize="small"
                      sx={{ color: "text.secondary" }}
                    />
                  </InputAdornment>
                ),
                endAdornment:
                  searchInput.length > 0 ? (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        edge="end"
                        aria-label="Clear search"
                        onClick={() => {
                          handleSearchInput("");
                        }}
                      >
                        <XIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ) : undefined,
              },
            }}
            // Uniform 280 px with the four filter pills — Christian,
            // 2026-05-13: *"make sure the arrangement is correct when
            // wrap to multiple rows; with uneven widths it looks
            // ugly."* Uniform widths give a symmetric 3+2 grid at the
            // typical 1440 viewport (main ≈ 1120 px after sidebar +
            // padding), and 5+0 on wider screens. Search field
            // narrower than 280 would re-introduce the typing-cramp
            // problem; wider would force a different wrap rhythm
            // than the filter pills.
            sx={{ width: 280 }}
          />
          <ProjectFilter
            value={filters.project_id}
            onChange={(ids) => {
              setFilters({ project_id: ids });
            }}
          />
          <SiteFilter
            value={filters.site_id}
            onChange={(ids) => {
              setFilters({ site_id: ids });
            }}
          />
          <Autocomplete<DataAcquisitionState, true>
            multiple
            disableCloseOnSelect
            size="small"
            options={[...DATA_ACQUISITION_STATES]}
            value={filters.state}
            onChange={(_e, values) => {
              setFilters({ state: values });
            }}
            getOptionLabel={formatInspectionState}
            // Fixed width so selected-state chips wrap inside the field
            // (height grows) instead of stretching the field wider —
            // keeps the toolbar layout stable when multiple statuses
            // are picked. Width sized so a single typical chip
            // ("Expert analysis" / "Pre screening") plus the MUI
            // input-cursor min-width tile on ONE line — Christian,
            // 2026-05-13: *"when I select the first item, a second
            // empty row appears. Just extend the height when it is
            // really required (2+ chips), not before."* The cursor
            // wrap previously happened at 220 px because MUI's
            // Autocomplete reserves ~80 px for the typing area; 280 px
            // gives ~180 px for the chip itself which fits every
            // instruction-state label.
            sx={{ width: 280 }}
            renderInput={(p) => (
              // Force the floating label to stay shrunk (top-left) so
              // Status matches the other filter pills (Project / Site /
              // Assignee) — Christian, 2026-05-13: *"the filter item
              // Status is different to the other filter items. The
              // string Status is inside the text field visible when
              // nothing is selected. They should match the same MUI
              // style like the other filter Fields (empty when nothing
              // is selected and the name of the filter 'Status' small
              // on the upper-left region of the field)."*
              //
              // The shared `AutocompleteInput` helper intentionally
              // leaves `shrink` un-forced because its analysis-surface
              // consumers (DefectsPanel / FindingsPanel) want the
              // placeholder-style label. Inline this one renderInput
              // so the toolbar pill matches /data-acquisitions' other
              // chips without changing the helper's defaults.
              <AutocompleteInput params={p} label="Status" shrink />
            )}
          />
          <AssigneeFilter
            value={filters.assignee_id}
            onChange={(ids) => {
              setFilters({ assignee_id: ids });
            }}
          />
          {/* FCII — link-status filter (All / Linked / Unlinked). Server-side
              via lyse/v1@8.3.0 `linkage`; surfaces field-created orphan DAs.
              Labelled "Linkage" (not "…status") so it doesn't collide with the
              "Status" combobox for screen-reader / by-role lookups. */}
          <SingleSelectFilter
            label="Linkage"
            value={filters.linkage}
            options={[
              { value: "all", label: "All", display: "" },
              { value: "linked", label: "Linked" },
              { value: "unlinked", label: "Unlinked" },
            ]}
            onChange={(next) => {
              setFilters({
                linkage: next as "all" | "linked" | "unlinked",
              });
            }}
          />
          {/* SB6-DA-01 — "Show completed". Off by default the list hides
              the terminal states (LOCKED, REJECTED); on shows everything
              the user can access. Mirrors the Contracts page toggle. */}
          <FormControlLabel
            sx={{ ml: 0, mr: 0, alignSelf: "center" }}
            control={
              <Switch
                checked={filters.includeCompleted}
                onChange={(_e, checked) => {
                  setFilters({ includeCompleted: checked });
                }}
                inputProps={{
                  "aria-label": "Show completed data acquisitions",
                }}
              />
            }
            label="Show completed"
          />
        </Stack>

        {/* Row 2 — table-settings: Dates → Export → Columns → Density.
            Christian, 2026-05-13: *"Dates and Export should be in the
            second row where the other table settings are sitting — the
            order should be 'Dates' 'Export' 'Columns' 'Density'."* The
            `borderTop` divider visually splits filter chips from
            settings, exactly like /sites. All four affordances are
            left-aligned (no spacer pushing one to the right edge —
            unlike /sites' Add-site primary CTA, /data-acquisitions has
            no row-creating action so there's nothing to anchor at the
            right edge). */}
        <Stack
          data-testid="data-acquisitions-toolbar-settings"
          direction="row"
          spacing={1}
          sx={(t) => ({
            mt: 1,
            pt: 1,
            // Divider line between row 1 (filters) and row 2 (settings).
            // `width: 100%` on both Stacks + the toolbar above keeps
            // this line spanning the full table column.
            borderTop: `1px solid ${t.palette.divider}`,
            width: "100%",
            alignItems: "center",
            flexWrap: "wrap",
          })}
        >
          <DateRangeButton
            dateFrom={filters.date_from}
            dateTo={filters.date_to}
            onChange={(next) => {
              setFilters(next);
            }}
          />
          <ExportButton params={buildQueryParams(filters)} />
          <ColumnVisibilityButton
            columns={columnDescriptors}
            model={columnVisibilityModel}
            onChange={handleColumnVisibilityChange}
          />
          <DensityToggleButton density={density} onChange={setDensity} />
        </Stack>
      </Box>

      {/* Grid region — owns the only scroll container on the page,
          horizontal AND vertical. `minWidth: 0` here is the second
          half of the column-flex pattern (the page wrapper's
          `overflowX: hidden` is the first) — without it, the inner
          flex item can grow to its content's intrinsic size and push
          the page wider than the viewport. */}
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0, minHeight: 0, display: "flex" }}>
          {isEmptyAfterLoad ? (
            <Box sx={{ flex: 1 }}>
              <EmptyState onClear={clearFilters} />
            </Box>
          ) : isInitialEmpty ? (
            <Stack
              role="region"
              aria-label="No data acquisitions"
              sx={{
                flex: 1,
                alignItems: "center",
                justifyContent: "center",
                py: 6,
              }}
            >
              <Typography color="text.secondary">
                No data acquisitions yet for this tenant.
              </Typography>
            </Stack>
          ) : (
            <Box
              role="region"
              aria-label="Data acquisitions"
              sx={{
                position: "relative",
                flex: 1,
                minWidth: 0,
                minHeight: 0,
              }}
            >
              <DataGrid<DataAcquisition>
                rows={items}
                columns={columns}
                loading={query.isPending}
                getRowId={(row) => row.id}
                density={density}
                aria-label="Data acquisitions"
                disableRowSelectionOnClick
                hideFooter
                paginationMode="server"
                rowCount={total}
                paginationModel={paginationModel}
                onPaginationModelChange={(model) => {
                  setFilters({
                    page: model.page + 1,
                    pageSize: model.pageSize,
                  });
                }}
                sortingMode="server"
                sortModel={sortModel}
                onSortModelChange={handleSortModelChange}
                columnVisibilityModel={columnVisibilityModel}
                onColumnVisibilityModelChange={handleColumnVisibilityChange}
                // R58-CC-01 (2026-05-19) — row-click only highlights;
                // the trailing per-row VIEW button (rendered in the
                // `__actions` column above) is the sole entry point
                // to /analysis. The previous `onRowClick` +
                // `onCellKeyDown` hooks are intentionally absent.
                pageSizeOptions={[25, 50, 100]}
                sx={{
                  // Borderless inside the column-flex region — the
                  // page-level layout owns the visual chrome, mirroring
                  // /sites which renders a bare HTML <table> (no Paper
                  // wrapper, no internal border).
                  border: "none",
                }}
              />
            </Box>
          )}
        </Box>

        {/* Footer — total LEFT, pagination + page-size RIGHT (AC3).
            Always rendered (even on empty states) so the geometric
            assertion has a stable target. */}
        <DataAcquisitionsFooter
          total={total}
          page={filters.page}
          pageSize={filters.pageSize}
          onPageChange={(p) => {
            setFilters({ page: p });
          }}
          onPageSizeChange={(n) => {
            setFilters({ page: 1, pageSize: n });
          }}
        />
      </Box>
      {/* DI-3-R1-FE-analyst — the sentence after a send-back (router state). */}
      <SentBackNotice />
    </Box>
  );
}

// Density toggle button (Story 055 AC2). Mirrors the /sites
// `DensityToggleButton` exactly — same icon swap, same visible-text
// label, same `aria-pressed` semantics — so the muscle memory built
// against /sites carries over to /data-acquisitions.
function DensityToggleButton({
  density,
  onChange,
}: {
  density: Density;
  onChange: (next: Density) => void;
}): ReactElement {
  const nextDensity: Density = density === "compact" ? "standard" : "compact";
  const stateSuffix = density === "compact" ? " (compact)" : " (standard)";
  const Icon = density === "compact" ? Rows3Icon : LayoutGridIcon;
  return (
    <Button
      size="small"
      variant="text"
      color="inherit"
      startIcon={<Icon fontSize="small" />}
      onClick={() => {
        onChange(nextDensity);
      }}
      aria-pressed={density === "compact"}
      title={`Switch to ${nextDensity} rows`}
    >
      Density{stateSuffix}
    </Button>
  );
}

const PAGE_SIZE_OPTIONS = [25, 50, 100] as const;

interface DataAcquisitionsFooterProps {
  total: number;
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
}

function DataAcquisitionsFooter({
  total,
  page,
  pageSize,
  onPageChange,
  onPageSizeChange,
}: DataAcquisitionsFooterProps): ReactElement {
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  // Clamp the visible page index against the (possibly) shrunk total
  // so Pagination's selected page stays in range when filters narrow
  // the result set under the current cursor.
  const visiblePage = Math.min(page, totalPages);
  const countText =
    total === 1
      ? "1 data acquisition in total"
      : `${String(total)} data acquisitions in total`;
  const handlePageSize = (e: SelectChangeEvent<number>): void => {
    onPageSizeChange(Number(e.target.value));
  };
  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      sx={(t) => ({
        flexShrink: 0,
        py: 1.5,
        gap: 2,
        borderTop: `1px solid ${t.palette.divider}`,
      })}
    >
      <Typography
        data-testid="data-acquisitions-footer-total"
        variant="body2"
        color="text.secondary"
      >
        {countText}
      </Typography>
      <Stack direction="row" alignItems="center" spacing={2}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography
            variant="body2"
            color="text.secondary"
            component="label"
            id="data-acquisitions-page-size-label"
          >
            Rows per page
          </Typography>
          <Select
            size="small"
            value={pageSize}
            onChange={handlePageSize}
            inputProps={{
              "aria-label": "Rows per page",
              "aria-labelledby": "data-acquisitions-page-size-label",
            }}
            sx={{ minWidth: 80 }}
          >
            {PAGE_SIZE_OPTIONS.map((n) => (
              <MenuItem key={n} value={n}>
                {n}
              </MenuItem>
            ))}
          </Select>
        </Stack>
        <Pagination
          page={visiblePage}
          count={totalPages}
          onChange={(_e, p) => {
            onPageChange(p);
          }}
          color="primary"
          aria-label="Pagination"
        />
      </Stack>
    </Stack>
  );
}
